import { type ComponentProps, useCallback } from 'react';
import { COMMON_MOBILE_BREAKPOINT } from '@elfsight/lib-utils';
import {
  ApplyFiltersButton,
  FiltersModal,
  formatCount,
  ModalTriggerButton,
  useFilters
} from '@elfsight/widget-filters';
import { useWindowWidth } from '@elfsight/widget-utils';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { useLocationsFilter } from '../../locations-filter-provider';
import { useAvailableFilters } from '../../utils/use-available-filters';
import { useWidgetContext } from '../../widget-context';
import { ClearButton } from './clear-button';
import { filterControls } from './controls';

type ApplyButtonProps = ComponentProps<typeof ApplyFiltersButton>;

function ApplyButton(props: ApplyButtonProps) {
  const { language } = useSettings();
  const { t } = useLingui();
  const { filteredLocationsCount } = useLocationsFilter();
  const formattedCount = formatCount(
    filteredLocationsCount,
    language?.language || 'en'
  );

  const renderApplyText = useCallback(
    ({
      filteredLocationsCount,
      formattedCount
    }: {
      filteredLocationsCount: number;
      formattedCount: string;
    }) => {
      if (filteredLocationsCount === 1) {
        return t`See ${formattedCount} Location`;
      }

      if (filteredLocationsCount > 1) {
        return t`See ${formattedCount} Locations`;
      }

      return t`No Results`;
    },
    [t]
  );

  return (
    <ApplyFiltersButton {...props} count={filteredLocationsCount}>
      {renderApplyText({ filteredLocationsCount, formattedCount })}
    </ApplyFiltersButton>
  );
}

export function PaneFilter() {
  const { clearSelectedFilters } = useFilters();
  const { availableFilters } = useAvailableFilters();
  const { widgetRef } = useWidgetContext();
  const width = useWindowWidth();
  const { t } = useLingui();

  const portalContainerElement =
    widgetRef?.current && width >= COMMON_MOBILE_BREAKPOINT
      ? widgetRef?.current
      : undefined;

  return (
    <FiltersModal
      type="pane"
      position="left"
      width={400}
      title={t`Filters`}
      withBackdrop={false}
      applyButton={<ApplyButton />}
      clearButton={<ClearButton />}
      trigger={<ModalTriggerButton>{t`Filters`}</ModalTriggerButton>}
      onClearRequest={clearSelectedFilters}
      portalContainerElement={portalContainerElement}
    >
      {availableFilters.map(({ type }) => {
        const Component = filterControls[type];

        return <Component key={type} setting={type} type="sidePanel" />;
      })}
    </FiltersModal>
  );
}
